import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { onPlatformPush } from "../services/pushNotifications";
import { showToast } from "../utils/toast";

const SUPPORTED_ROLES = new Set(["CUSTOMER", "CAREGIVER", "CARE_MANAGER", "ADMIN"]);

const refreshListeners = new Set();
const optimisticListeners = new Set();

function notifyRefresh() {
  refreshListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function applyOptimisticRead(ids) {
  const idSet = new Set(
    (Array.isArray(ids) ? ids : [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0)
  );
  if (!idSet.size) return;
  const readAt = new Date().toISOString();
  optimisticListeners.forEach((fn) => {
    try {
      fn(idSet, readAt);
    } catch {
      /* ignore */
    }
  });
}

function applyOptimisticReadAll() {
  const readAt = new Date().toISOString();
  optimisticListeners.forEach((fn) => {
    try {
      fn(null, readAt);
    } catch {
      /* ignore */
    }
  });
}

function countUnread(list) {
  return list.filter((n) => !n.readAt).length;
}

/**
 * Loads in-app + platform notifications for the signed-in user (all supported roles).
 * Multiple components share refresh + optimistic updates so the toolbar badge stays in sync.
 */
export function refreshAllNotifications() {
  notifyRefresh();
}

export function useNotifications({ limit = 100, pollMs = 45000 } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const knownUnreadRef = useRef(null);

  const load = useCallback(async () => {
    if (!api.getToken?.()) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    const role = api.getAuthRoleFromToken?.();
    if (!SUPPORTED_ROLES.has(role)) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    try {
      const res = await api.listMyNotifications(limit);
      const list = Array.isArray(res?.notifications) ? res.notifications : [];
      const nextUnread =
        typeof res?.unreadCount === "number" ? res.unreadCount : countUnread(list);
      if (knownUnreadRef.current != null && nextUnread > knownUnreadRef.current) {
        const latest = [...list]
          .filter((n) => !n.readAt)
          .sort(
            (a, b) =>
              new Date(b.sentAt || b.createdAt || 0).getTime() -
              new Date(a.sentAt || a.createdAt || 0).getTime()
          )[0];
        if (latest?.title) {
          const body = latest.body ? `: ${latest.body}` : "";
          showToast.info(`${latest.title}${body}`);
        }
      }
      knownUnreadRef.current = nextUnread;
      setNotifications(list);
      setUnreadCount(nextUnread);
      setError("");
    } catch (err) {
      setError(err?.message || "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    const onOptimistic = (idSet, readAt) => {
      setNotifications((prev) => {
        const next = prev.map((n) => {
          if (idSet && !idSet.has(Number(n.id))) return n;
          if (n.readAt) return n;
          return { ...n, readAt };
        });
        setUnreadCount(countUnread(next));
        return next;
      });
    };
    optimisticListeners.add(onOptimistic);
    return () => optimisticListeners.delete(onOptimistic);
  }, []);

  useEffect(() => {
    setLoading(true);
    load().catch(() => {});
    refreshListeners.add(load);
    const unsubPush = onPlatformPush(() => load());
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    if (!pollMs || pollMs < 5000) {
      return () => {
        unsubPush();
        document.removeEventListener("visibilitychange", onVisible);
        refreshListeners.delete(load);
      };
    }
    const timer = window.setInterval(load, pollMs);
    return () => {
      window.clearInterval(timer);
      unsubPush();
      document.removeEventListener("visibilitychange", onVisible);
      refreshListeners.delete(load);
    };
  }, [load, pollMs]);

  const markRead = useCallback(async (ids) => {
    const clean = (Array.isArray(ids) ? ids : [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!clean.length) return 0;
    applyOptimisticRead(clean);
    try {
      const res = await api.markNotificationsRead(clean);
      const updated = Number(res?.updated) || 0;
      await load();
      notifyRefresh();
      return updated;
    } catch {
      await load();
      notifyRefresh();
      return 0;
    }
  }, [load]);

  const markAllRead = useCallback(async () => {
    applyOptimisticReadAll();
    try {
      const res = await api.markAllNotificationsRead();
      await load();
      notifyRefresh();
      return Number(res?.updated) || 0;
    } catch {
      await load();
      notifyRefresh();
      return 0;
    }
  }, [load]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    load,
    markRead,
    markAllRead,
  };
}
